import axios, { AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import {
  GetPublicLineWorkspaceTicketsResponse,
  LineWorkspaceTicket,
} from '../@interfaces/ticket/api';
import { TicketUnitTypeCode } from '../@interfaces/ticket/ticket-unit-type';
import { getTicketTypeByUnitType } from '../features/mypage/components/ticketType';
import { MyPageTicket } from '../features/mypage/components/types';

// LineWorkspaceTicketをMyPageTicketに変換する関数
const convertToMyPageTicket = (ticket: LineWorkspaceTicket): MyPageTicket => {
  // 期限切れ判定
  const expiryDate = dayjs(ticket.expireDate, 'YYYY/MM/DD');
  const daysUntilExpiry = expiryDate.diff(dayjs(), 'day');
  const isExpiringSoon = daysUntilExpiry <= 10 && daysUntilExpiry >= 0;

  // unitSuffixの変換処理（管理画面と同じロジックを適用）
  let displayUnitSuffix = ticket.unitSuffix;

  // APIから返されるunitTypeがない場合は、unitSuffixから推定
  if (ticket.unitSuffix === '時間') {
    // timeタイプの場合、管理画面と同様に「分」として表示
    displayUnitSuffix = '分';
  }

  // 残数が全体の1/3以下かどうかを判定
  const isLowRemaining = ticket.remainingCount <= (ticket.totalCount || 0) / 3;

  // expireDateをハイフン区切りからスラッシュ区切りに変換
  const formattedExpireDate = ticket.expireDate.replace(/-/g, '/');

  return {
    ...ticket,
    expireDate: formattedExpireDate,
    unitSuffix: displayUnitSuffix || '',
    unitType: ticket.unitType as TicketUnitTypeCode,
    ticketType: getTicketTypeByUnitType(ticket.unitType as TicketUnitTypeCode),
    isExpiringSoon,
    isLowRemaining,
    description: ticket.description || undefined,
    cancelPolicy: ticket.cancelPolicy || undefined,
  };
};

export const useLineWorkspaceTickets = (
  workspaceUid: string,
  lineSessionKey?: string
) => {
  const [data, setData] = useState<GetPublicLineWorkspaceTicketsResponse>();
  const [convertedTickets, setConvertedTickets] = useState<MyPageTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!workspaceUid || !lineSessionKey) {
      console.log('Skipping workspace tickets fetch - missing params:', {
        workspaceUid,
        lineSessionKey,
      });
      return;
    }

    setLoading(true);
    setError(undefined);

    const url = `${
      process.env.REACT_APP_API_END_POINT
    }/public/ws/${workspaceUid}/line/workspace-tickets?lineSessionKey=${encodeURIComponent(
      lineSessionKey
    )}`;

    axios
      .get<GetPublicLineWorkspaceTicketsResponse>(url)
      .then(
        (response: AxiosResponse<GetPublicLineWorkspaceTicketsResponse>) => {
          setData(response.data);
          // LineWorkspaceTicketをMyPageTicketに変換
          if (response.data.tickets) {
            const converted = response.data.tickets.map(convertToMyPageTicket);
            setConvertedTickets(converted);
          }
        }
      )
      .catch((err) => {
        setError(err as Error);
        console.error('Failed to fetch line workspace tickets:', err);
        setConvertedTickets([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [workspaceUid, lineSessionKey]);

  return {
    data, // 全体のレスポンスデータ
    tickets: data?.tickets, // 元のLineWorkspaceTicket[]
    convertedTickets, // 変換されたMyPageTicket[]
    usageLogs: data?.usageLogs,
    isLoading: loading,
    error,
  };
};
